import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { atom, useAtomValue } from "jotai";
import { Suspense, useEffect, useRef, useState } from "react";
import { ScrollView, TextInput } from "react-native";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { SubtleInput } from "@whoosh/design-system/src/components/controls/SubtleInput";
import { Loader } from "@whoosh/design-system/src/components/display";
import {
  EyeClosedIcon,
  EyeIcon,
  InfoIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  Text,
  Pressable,
  Box,
} from "@whoosh/design-system/src/components/primitive";

import { PasswordResetInput } from "../../../../../../../../../screens/Register/hooks/__generated__/useRegisterNewAccountMutation.graphql";
import { useRegisterNewAccount } from "../../../../../../../../../screens/Register/hooks/useRegisterNewAccount";
import { VerificationModalStepStateAtom } from "../../../../atoms/stores";
import { SetPasswordStepQuery } from "./__generated__/SetPasswordStepQuery.graphql";

const setPasswordDataAtom = atom((get) => {
  const curStateData = get(VerificationModalStepStateAtom);
  if (curStateData.step === "SetPassword") {
    return curStateData;
  }
  return null;
});

type Props = {
  clubSlug?: string;
};

export const SetPasswordStep = ({ clubSlug }: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<SetPasswordStepQuery>(
    SetPasswordStepQueryString
  );

  useEffect(() => {
    if (clubSlug) {
      loadQuery({ clubSlug: clubSlug });
    }
  }, [clubSlug, loadQuery]);

  return (
    <Suspense fallback={<Loader />}>
      {queryRef ? (
        <SetPasswordInternal queryRef={queryRef} clubSlug={clubSlug} />
      ) : null}
    </Suspense>
  );
};

const SetPasswordInternal = ({
  queryRef,
  clubSlug,
}: {
  queryRef: PreloadedQuery<SetPasswordStepQuery>;
  clubSlug?: string;
}) => {
  const data = usePreloadedQuery(SetPasswordStepQueryString, queryRef);
  const [inputIsPassword, setInputIsPassword] = useState(true);
  const [curPassword, setCurPassword] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<TextInput>(null);

  const curStepData = useAtomValue(setPasswordDataAtom);

  const canSubmit = !!curStepData?.verifiedEmail && curPassword.length >= 8;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputIsPassword]);

  const [doRegister, isRegistering] = useRegisterNewAccount();

  const onRegister = () => {
    if (curStepData?.token) {
      const input: PasswordResetInput = {
        token: curStepData.token,
        password: curPassword,
        passwordConfirmation: curPassword,
        userContext: clubSlug,
      };
      doRegister(input);
    }
  };

  const inputHasError = !canSubmit && !isFocused;

  return (
    <Box flex={1} paddingHorizontal={6}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <VStack paddingBottom={6} flexGrow={1}>
          <VStack flexGrow={1} mb={6}>
            <Text
              marginBottom={4}
              variant="mainSmallMedium"
              color="text-secondary"
            >
              {curStepData?.verifiedEmail}
            </Text>
            <Text variant={"mainTitle2"}>{t`Set a password`}</Text>
            <Text
              marginBottom={1}
              marginTop={4}
              variant="mainSmallMedium"
              color="text-secondary"
            >
              {t`Welcome to ${
                data.unauthenticatedClub?.name ?? ""
              }! Set your password to enter your account.`}
            </Text>
          </VStack>
          <SubtleInput
            inputRef={inputRef}
            placeholder={t`Your Password`}
            name="password"
            type={inputIsPassword ? "password" : "text"}
            value={curPassword}
            onChange={setCurPassword}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            hasError={inputHasError}
            rightIcon={
              <Pressable
                onPress={() => {
                  setInputIsPassword((val) => !val);
                }}
              >
                {inputIsPassword ? <EyeIcon /> : <EyeClosedIcon />}
              </Pressable>
            }
          />
          <Box marginTop={4}>
            <Box flexDirection="row" gap={1}>
              <InfoIcon color="text-secondary" />
              <Text variant="mainTiny" color="text-secondary">
                {t`At least 8 characters`}
              </Text>
            </Box>
          </Box>
        </VStack>
      </ScrollView>
      <VStack>
        <Button
          isDisabled={!canSubmit}
          onPress={onRegister}
          isLoading={isRegistering}
          accessibilityLabel={t`Set Password button`}
        >{t`Set Password`}</Button>
      </VStack>
    </Box>
  );
};

export const SetPasswordStepQueryString = graphql`
  query SetPasswordStepQuery($clubSlug: String!) {
    unauthenticatedClub(slug: $clubSlug) {
      name
    }
  }
`;
